import { forwardRef } from 'react';

import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { cn } from 'components/utils';

import {
  checkboxBoxRecipe,
  checkboxControlRecipe,
  checkboxGlyphRecipe,
  checkboxLabelRecipe,
  checkboxRootRecipe,
} from './Checkbox.recipe';

import type { CheckboxProps } from './types';

/**
 * Design System 2.0 checkbox.
 *
 * Ported from Figma node 585:57 (file 5aSkE0U4TeWbuIZDvccHjc). Props mirror the variant axes that
 * are real inputs — `checked`, `indeterminate`, `disabled`, `label` — while `State` is observed from
 * the DOM rather than passed, because Base UI already tracks it.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, so
 * the same markup is correct in both themes.
 *
 * The wrapper is a real `<label>`, which is Base UI's documented shape for this component: the
 * hidden `<input>` lives inside `Checkbox.Root`, so wrapping both it and the text makes the text a
 * click target and gives the control its accessible name with no `id` plumbing. `Checkbox.Root`
 * itself renders a focusable `<span role="checkbox">` — the visible box is a child of it, since the
 * focus halo needs a bigger radius than the box and one element cannot have two.
 *
 * Geometry, the state machine and the reason the focus treatment is a background rather than an
 * outline are all in `Checkbox.recipe`.
 */
export const Checkbox = forwardRef<HTMLElement, CheckboxProps>(
  ({ label, className, rootClassName, labelClassName, disabled, ...rest }, ref) => (
    <label className={cn(checkboxRootRecipe({ disabled }), rootClassName)}>
      <BaseCheckbox.Root ref={ref} className={cn(checkboxControlRecipe(), className)} disabled={disabled} {...rest}>
        {/* `data-part` names the box so a test can assert on it without depending on a styling
            class, as `TextInput` does for its bordered box. */}
        <span className={checkboxBoxRecipe()} data-part="box" />

        {/**
         * Mounted only while the box is ticked or indeterminate, which Base UI handles.
         *
         * Both glyphs are always in the DOM and swapped in CSS rather than chosen from the
         * `indeterminate` prop, so a mixed state driven from somewhere we cannot see — a
         * `CheckboxGroup` parent, say — still draws the right one.
         *
         * The paths are Figma's own, in Figma's own 24x24 viewBox: a 1.5px round-capped stroke is
         * the glyph, so there is no filled icon in the app's set that could stand in for it, and
         * copying the vector is the only way to be sure the tick is the tick that was drawn.
         */}
        <BaseCheckbox.Indicator className={checkboxGlyphRecipe()}>
          <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              className="group-data-[indeterminate]/cb:hidden"
              d="M8 12.4286L10.4 15L16 9"
              data-part="tick"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
            <path
              className="hidden group-data-[indeterminate]/cb:block"
              d="M8 12H16"
              data-part="dash"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
          </svg>
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>

      {label !== undefined && <span className={cn(checkboxLabelRecipe({ disabled }), labelClassName)}>{label}</span>}
    </label>
  )
);
